import { z } from "zod";

/**
 * Google Books API response types
 * Based on Google Books API v1 volumes endpoint
 */

/**
 * Industry identifier (ISBN, etc.)
 */
export const GoogleBooksIndustryIdentifierSchema = z.object({
    identifier: z.string(),
    type: z.string(),
});

export type GoogleBooksIndustryIdentifier = z.infer<typeof GoogleBooksIndustryIdentifierSchema>;

/**
 * Image links from Google Books
 */
export const GoogleBooksImageLinksSchema = z.object({
    extraLarge: z.string().url().optional(),
    large: z.string().url().optional(),
    medium: z.string().url().optional(),
    small: z.string().url().optional(),
    smallThumbnail: z.string().url().optional(),
    thumbnail: z.string().url().optional(),
});

export type GoogleBooksImageLinks = z.infer<typeof GoogleBooksImageLinksSchema>;

/**
 * Volume info from Google Books API
 */
export const GoogleBooksVolumeInfoSchema = z.object({
    authors: z.array(z.string()).optional(),
    canonicalVolumeLink: z.string().url().optional(),
    description: z.string().optional(),
    imageLinks: GoogleBooksImageLinksSchema.optional(),
    industryIdentifiers: z.array(GoogleBooksIndustryIdentifierSchema).optional(),
    infoLink: z.string().url().optional(),
    language: z.string().optional(),
    previewLink: z.string().url().optional(),
    publishedDate: z.string().optional(),
    publisher: z.string().optional(),
    title: z.string(),
});

export type GoogleBooksVolumeInfo = z.infer<typeof GoogleBooksVolumeInfoSchema>;

/**
 * Individual volume from Google Books API
 */
export const GoogleBooksVolumeSchema = z.object({
    accessInfo: z
        .object({
            country: z.string().optional(),
            embeddable: z.boolean().optional(),
            publicDomain: z.boolean().optional(),
            viewability: z.string().optional(),
        })
        .optional(),
    etag: z.string().optional(),
    id: z.string(),
    saleInfo: z
        .object({
            country: z.string().optional(),
            isEbook: z.boolean().optional(),
            saleability: z.string().optional(),
        })
        .optional(),
    selfLink: z.string().url().optional(),
    volumeInfo: GoogleBooksVolumeInfoSchema,
});

export type GoogleBooksVolume = z.infer<typeof GoogleBooksVolumeSchema>;

/**
 * Google Books API search response
 */
export const GoogleBooksSearchResponseSchema = z.object({
    items: z.array(GoogleBooksVolumeSchema).optional(),
    kind: z.string(),
    totalItems: z.number().int().min(0),
});

export type GoogleBooksSearchResponse = z.infer<typeof GoogleBooksSearchResponseSchema>;

/**
 * Transformed book data from Google Books for our use
 */
export interface TransformedGoogleBook {
    title: string;
    author: string;
    isbn?: string;
    description?: string;
    coverUrl?: string;
    publisher?: string;
    publishedDate?: string;
}
